import {
  Button
} from 'reactstrap'
import { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import TlGoogleMapsApi from 'api/tl-google-maps-api'
import { GoogleMap, Autocomplete } from '@react-google-maps/api'
import style from './user-address-pinpoint.module.scss'
import pinpointIcon from 'assets/img/current-location.png'

const propTypes = {
  provinceName        : PropTypes.string,
  cityName            : PropTypes.string,
  districtName        : PropTypes.string,
  fullAddress         : PropTypes.string,
  lat                 : PropTypes.number,
  long                : PropTypes.number,
  setFieldValue       : PropTypes.func,
  onSubmitCoordinates : PropTypes.func,
  closePinpoint       : PropTypes.func.isRequired,
}

/**
 * Component for user to pick lat and long of their address from GoogleMaps
 * @param {string} provinceName - to find initial center location based on province name, will be used if lat and long value empty
 * @param {string} cityName - to find initial center location based on city name, will be used if lat and long value empty
 * @param {string} districtName - to find initial center location based on district name, will be used if lat and long value empty
 * @param {number} lat - initial lat value for center pointer
 * @param {number} long - initial long value for center pointer
 * @param {function} setFieldValue - formik function
 * @param {function} closePinpoint - close pinpoint function
 */
const UserAddressPinpoint = ({
  provinceName,
  cityName,
  districtName,
  fullAddress,
  lat,
  long,
  setFieldValue,
  onSubmitCoordinates,
  closePinpoint
}) => {
  const mapRef = useRef(null)

  const [ isFetchingLocation, setIsFetchingLocation ]       = useState(true)
  const [ isGettingUserLocation, setIsGettingUserLocation ] = useState(false)

  const [ searchAddressResult, setSearchAddressResult ] = useState(null)

  // set default location near tinkerlust in case fetching map error from api
  const [ userLocation, setUserLocation ] = useState({
    lat : parseFloat(process.env.REACT_APP_DEFAULT_LATITUDE),
    lng : parseFloat(process.env.REACT_APP_DEFAULT_LONGITUDE)
  })

  const showGoogleMap = !isFetchingLocation

  useEffect(() => {
    setIsFetchingLocation(true)

    const hasInitialLatLng = Number.isFinite(lat) && Number.isFinite(long)

    if (hasInitialLatLng) {
      // need to setUserLocation here because somehow google maps not re-center the map
      // if we put lat and long directly to userLocation state initial value
      setUserLocation({
        lat,
        lng: long
      })
      setIsFetchingLocation(false)
      return
    }

    // no need to fetch google api if lat and long exist
    // fetch initial center value to google maps api based on province, city value
    // by doing this, it will be easier for user to find their address
    const addressParts = [
      (fullAddress || '').trim(),
      (districtName || '').trim(),
      (cityName || '').trim(),
      (provinceName || '').trim(),
      'Indonesia'
    ].filter(Boolean)

    if (addressParts.length === 0) {
      setIsFetchingLocation(false)
      return
    }

    const userAddress = addressParts
      .map((part) => encodeURIComponent(part))
      .join(', ')

    TlGoogleMapsApi.getUserAreaLocation({ userAddress })
      .then((response) => {
        const geocodeData = response
        const geometry = geocodeData?.results?.[0]?.geometry?.location

        if (!geometry) return

        setUserLocation({
          lat: geometry.lat,
          lng: geometry.lng
        })
      })
      .catch((error) => {
        console.log(error)
      })
      .finally(() => setIsFetchingLocation(false))
  }, [ lat, long, fullAddress, provinceName, cityName, districtName ])

  const handleDragEnd = () => {
    if (!mapRef.current?.center) return

    const newLat = mapRef.current.center.lat()
    const newLng = mapRef.current.center.lng()

    setUserLocation({
      lat : newLat,
      lng : newLng
    })
  }

  const handleSubmitPinpoint = () => {
    if (!mapRef.current?.center) return

    const newLat = mapRef.current.center.lat()
    const newLng = mapRef.current.center.lng()

    if (typeof onSubmitCoordinates === 'function') {
      onSubmitCoordinates({
        latitude: newLat,
        longitude: newLng,
      })
    } else if (typeof setFieldValue === 'function') {
      setFieldValue('latitude', newLat)
      setFieldValue('longitude', newLng)
    }

    setUserLocation({
      lat : newLat,
      lng : newLng
    })

    closePinpoint()
  }

  const handleUseMyLocationClick = () => {
    // get lat and long using current address location
    // user need to allow browser access their location
    if (navigator.geolocation) {
      setIsGettingUserLocation(true)

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latitude  = position.coords.latitude
          const longitude = position.coords.longitude

          setUserLocation({
            lat : latitude,
            lng : longitude
          })
        },
        (error) => {
          // show alert if error happen when getting user location
          // e.g error when user is not allowing our app to access their location
          alert('Terjadi error saat mendapatkan lokasi.')
          console.error('Error getting geolocation:', error)
          setIsGettingUserLocation(false)
        }
      )
    } else {
      // show alert if browser is not supported
      const alertMsg = [
        'Fitur geolocation tidak didukung oleh browser ini.',
        'Mohon coba gunakan browser lain dan coba lagi.'
      ].join('\n\n')
      alert(alertMsg)
    }
  }

  const onSearchAddressLoad = (autocomplete) => {
    setSearchAddressResult(autocomplete)
  }

  const onSearchAddressChange = () => {
    if (searchAddressResult != null) {
      const place = searchAddressResult.getPlace()
      const geometry = place?.geometry
      if (!geometry) return

      const newLat = geometry.location.lat()
      const newLng = geometry.location.lng()

      setUserLocation({
        lat : newLat,
        lng : newLng
      })
    }
  }

  // to disable trigger google maps function on enter press
  const onEnterKeyPress = (event) => {
    if (event.keyCode === 13) {
      event.preventDefault()
    }
  }

  return (
    <>
      {showGoogleMap && (
        <GoogleMap
          zoom={20}
          center={userLocation}
          mapContainerClassName={`w-100 ${style.mapContainer}`}
          options={{
            fullscreenControl : false,
            streetViewControl : false,
            mapTypeControl    : false,
            // to allow dragging with 1 finger in mobile view
            gestureHandling   : 'greedy'
          }}
          onLoad={(map) => { mapRef.current = map }}
          onDragEnd={handleDragEnd}
          // re-enable button `Gunakan Lokasi saya` after navigated to current location and map fully loaded
          onTilesLoaded={() => setIsGettingUserLocation(false)}
        >
          <div className={`px-3 ${style.searchFormLocation}`}>
            <Autocomplete
              onPlaceChanged={onSearchAddressChange}
              onLoad={onSearchAddressLoad}
              className='d-flex align-items-center shadow'
            >
              <input
                id='address-pinpoint-search-field'
                className='form-control border-0'
                type='text'
                placeholder='Cari alamatmu disini'
                onKeyDown={onEnterKeyPress}
                onKeyUp={onEnterKeyPress}
              />
            </Autocomplete>
          </div>

          <div className={`d-flex flex-column align-items-center ${style.pinpointIcon}`}>
            <strong className='mb-1 bg-danger p-1 text-white rounded'>
              {'Alamatmu disini'}
            </strong>
            <img
              width={27}
              height={40}
              src={pinpointIcon}
              alt={'current location'}
            />
          </div>

          <Button
            color='white'
            onClick={handleUseMyLocationClick}
            className={`bg-white rounded font-weight-bold shadow ${style.myLocationButton}`}
            disabled={isGettingUserLocation}
          >
            <i className='fa fa-map-marker mr-2 text-primary' />
            {'Gunakan Lokasi Saya'}
          </Button>
        </GoogleMap>
      )}

      <div className='p-2'>
        <Button
          color='primary'
          block
          onClick={handleSubmitPinpoint}
        >
          {'Pilih Lokasi'}
        </Button>

        <Button
          outline
          block
          color='primary'
          onClick={closePinpoint}
        >
          {'Kembali'}
        </Button>
      </div>
    </>
  )
}

UserAddressPinpoint.propTypes = propTypes

export default UserAddressPinpoint